import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CurrencyPicker from "@/components/CurrencyPicker";
import { PageHeader, StatusBadge } from "@/components/Shared";
import { Package, WarningOctagon, XCircle, Coins, ArrowUp, ArrowDown, Sliders, ArrowRight } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";


const TX_ICON = { restock: ArrowUp, usage: ArrowDown, sale: ArrowDown, adjustment: Sliders };

function Kpi({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    danger: "text-destructive bg-destructive/10",
    warn: "text-warning bg-warning/10",
    primary: "text-primary bg-primary/10",
    success: "text-success bg-success/10",
  };
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-md p-5 h-full flex flex-col justify-between transition-colors duration-200 hover:border-primary/40">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium">{label}</span>
        <span className={`h-8 w-8 rounded-md flex items-center justify-center ${tones[tone]}`}><Icon size={17} weight="bold" /></span>
      </div>
      <p className="text-3xl font-heading font-semibold tracking-tight mt-4 tnums">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { money } = useAuth();
  const [stats, setStats] = useState(null);

  const load = () => api.get("/dashboard/stats").then(({ data }) => setStats(data));
  useEffect(() => { load(); }, []);

  if (!stats) {
    return <div className="p-8"><div className="h-6 w-40 bg-muted rounded animate-pulse" /></div>;
  }

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Live overview of your prosthetics & orthotics inventory"><CurrencyPicker /></PageHeader>
      <div className="p-6 md:p-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Kpi testid="kpi-out" icon={XCircle} label="Out of Stock" value={stats.out_of_stock} tone="danger" />
          <Kpi testid="kpi-low" icon={WarningOctagon} label="Low Stock" value={stats.low_stock} tone="warn" />
          <Kpi testid="kpi-total" icon={Package} label="Total Items" value={stats.total_items} tone="primary" />
          <Kpi testid="kpi-value" icon={Coins} label="Inventory Value" value={money(stats.inventory_value)} tone="success" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-card border border-border rounded-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-heading font-medium">Needs attention</h3>
              <Link to="/inventory/alerts" className="text-sm text-primary hover:underline flex items-center gap-1">View alerts <ArrowRight size={14} /></Link>
            </div>
            {stats.attention_items.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Everything is well stocked. No action needed.</p>
            ) : (
              <div className="divide-y divide-border">
                {stats.attention_items.map((it) => (
                  <div key={it.id} className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium">{it.name}</p>
                      <p className="text-xs text-muted-foreground tnums">{it.quantity} {it.unit} · min {it.min_threshold}</p>
                    </div>
                    <StatusBadge status={it.status} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-md p-6">
            <h3 className="text-lg font-heading font-medium mb-5">By category</h3>
            {stats.category_breakdown.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No items yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.category_breakdown} layout="vertical" margin={{ left: 0, right: 16 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="category" width={110} tick={{ fontSize: 11, fill: "hsl(150 6% 42%)" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "hsl(150 8% 95%)" }} contentStyle={{ borderRadius: 4, border: "1px solid hsl(120 8% 89%)", fontSize: 12 }} />
                  <Bar dataKey="count" radius={[0, 3, 3, 0]} barSize={16}>
                    {stats.category_breakdown.map((_, i) => <Cell key={i} fill="hsl(155 24% 33%)" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-6">
          <h3 className="text-lg font-heading font-medium mb-5">Recent activity</h3>
          {stats.recent_transactions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No stock movements recorded yet.</p>
          ) : (
            <div className="divide-y divide-border">
              {stats.recent_transactions.map((tx) => {
                const Icon = TX_ICON[tx.type] || Sliders;
                const debit = tx.quantity_change < 0;
                return (
                  <div key={tx.id} className="flex items-center gap-3 py-3">
                    <span className={`h-8 w-8 rounded-md flex items-center justify-center ${debit ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}`}>
                      <Icon size={15} weight="bold" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{tx.item_name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{tx.type} · {tx.user_name}</p>
                    </div>
                    <span className={`text-sm font-medium tnums ${debit ? "text-destructive" : "text-success"}`}>
                      {debit ? "" : "+"}{tx.quantity_change}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
